import React, { useState, useEffect } from 'react';
import { orderService } from '../../services/api';
import { Eye, RefreshCw, Search, X, Package } from 'lucide-react';
import InvoicePrint from './InvoicePrint';

function OrdersManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const params = {
          per_page: 50,
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(searchTerm ? { search: searchTerm } : {}),
        };
        const data = await orderService.index(params);
        if (isMounted) {
          setOrders(Array.isArray(data) ? data : data?.data || []);
        }
      } catch (error) {
        console.error('Error loading orders:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [statusFilter, searchTerm, refreshKey]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      setUpdatingOrderId(orderId);
      await orderService.updateStatus(orderId, newStatus);
      setRefreshKey((key) => key + 1);
    } catch (error) {
      console.error('Error updating order status:', error);
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-red-100 text-red-800',
    processing: 'bg-blue-100 text-blue-800',
    shipped: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    refunded: 'bg-gray-100 text-gray-800',
  };

  const paymentStatusColors = {
    paid: 'bg-red-100 text-red-800',
    pending: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
    refunded: 'bg-gray-100 text-gray-800',
  };

  const statusOptions = ['all', 'pending', 'accepted', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Orders Management</h2>
          <p className="text-sm text-gray-500">Monitor order flow, payments, and fulfillment in real time.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status === 'all' ? 'All statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <input
              type="text"
              placeholder="Search by order #, email, phone"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button type="submit" className="bg-gray-900 text-white px-4 py-2 rounded-md text-sm">
              Search
            </button>
          </form>
          <button
            onClick={() => setRefreshKey((key) => key + 1)}
            className="border border-gray-300 rounded-md px-4 py-2 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total (₹)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Placed</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.length > 0 ? (
              orders.map((order) => {
                const customerName = order.user?.name || order.shipping_address?.full_name || order.email || 'Guest checkout';
                const contact = order.user?.email || order.email || order.phone || '—';
                const createdAt = order.created_at ? new Date(order.created_at).toLocaleDateString() : '—';
                return (
                  <tr key={order.id}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{order.order_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">{customerName}</div>
                      {order.user?.id && <div className="text-xs text-gray-500">User #{order.user.id}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{contact}</td>
                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">₹{order.total || '0.00'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${paymentStatusColors[order.payment_status] || 'bg-gray-100 text-gray-800'}`}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{createdAt}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                          className={`border border-gray-300 rounded-md px-2 py-1 text-xs font-bold transition-colors ${statusColors[order.status] || 'bg-gray-100'}`}
                          disabled={updatingOrderId === order.id}
                        >
                          {statusOptions.filter((status) => status !== 'all').map((status) => (
                            <option key={status} value={status} className="bg-white text-gray-900">
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => setDetailOrder(order)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="View Order Details"
                        >
                          <Package size={18} />
                        </button>
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="View Invoice"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" className="px-6 py-10 text-center text-gray-500">
                  {loading ? 'Fetching orders...' : 'No orders found matching your criteria'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedOrder && (
        <InvoicePrint
          invoice={{
            ...selectedOrder,
            invoice_number: selectedOrder.order_number,
            issued_at: selectedOrder.created_at,
            order: selectedOrder,
          }}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      {detailOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Order Details: {detailOrder.order_number}</h3>
                <p className="text-sm text-gray-500">Placed on {detailOrder.created_at ? new Date(detailOrder.created_at).toLocaleString() : '—'}</p>
              </div>
              <button onClick={() => setDetailOrder(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Customer & Status Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Customer</h4>
                  <p className="font-bold text-gray-900 text-sm">{detailOrder.user?.name || detailOrder.shipping_address?.full_name || 'Guest'}</p>
                  <p className="text-xs text-gray-500">{detailOrder.email || '—'}</p>
                  <p className="text-xs text-gray-500">{detailOrder.phone || '—'}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Shipping Address</h4>
                  {detailOrder.shipping_address ? (
                    <div className="text-xs text-gray-600 leading-relaxed">
                      <p className="font-semibold">{detailOrder.shipping_address.full_name}</p>
                      <p>{detailOrder.shipping_address.address_line1 || detailOrder.shipping_address.address_line_1}</p>
                      <p>{detailOrder.shipping_address.city}, {detailOrder.shipping_address.state} {detailOrder.shipping_address.postal_code}</p>
                      <p>{detailOrder.shipping_address.country}</p>
                    </div>
                  ) : <p className="text-xs text-gray-400">N/A</p>}
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Payment</h4>
                  <p className="text-sm font-bold text-gray-900 uppercase">{detailOrder.payment_method}</p>
                  <p className="text-xs text-gray-500">Status: {detailOrder.payment_status}</p>
                  <p className="text-xs text-gray-500">Order: {detailOrder.status}</p>
                </div>
              </div>

              {/* Order Items with Variant Info */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Items to Ship</h4>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Variant</th>
                        <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase text-center">Qty</th>
                        <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase text-right">Price</th>
                        <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detailOrder.items && detailOrder.items.length > 0 ? (
                        detailOrder.items.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {item.product?.featured_image && (
                                  <img src={item.product.featured_image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                )}
                                <div>
                                  <p className="font-bold text-gray-900 text-sm">{item.product?.name || item.product_name}</p>
                                  <p className="text-xs text-gray-400">SKU: {item.product_sku || item.product?.sku || 'N/A'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {item.variant_id || item.variant ? (
                                <span className="inline-flex items-center px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                                  {item.variant?.name || item.product_name}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center font-bold text-gray-900">{item.quantity}</td>
                            <td className="px-4 py-3 text-right text-gray-700 text-sm">₹{Number(item.unit_price || item.price || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-right font-bold text-gray-900">₹{Number(item.total_price || item.subtotal || 0).toFixed(2)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400 text-sm">No items found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span className="font-bold text-gray-900">₹{Number(detailOrder.subtotal || 0).toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">GST</span><span className="font-bold text-gray-900">₹{Number(detailOrder.tax || 0).toFixed(2)}</span></div>
                  {Number(detailOrder.discount || 0) > 0 && (
                    <div className="flex justify-between text-sm text-red-600"><span>Discount</span><span className="font-bold">-₹{Number(detailOrder.discount || 0).toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-200"><span className="font-black text-gray-900">Total</span><span className="font-black text-red-600 text-lg">₹{Number(detailOrder.total || 0).toFixed(2)}</span></div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setDetailOrder(null)} className="px-6 py-2.5 border border-gray-300 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition">Close</button>
              <button onClick={() => { setDetailOrder(null); setSelectedOrder(detailOrder); }} className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition shadow-lg shadow-red-200">View Invoice</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersManagement;
